import { useState } from "react";
import { useDrippers } from "../hooks/useDrippers";
import { api } from "../lib/api";
import { DRIPPER_MATERIALS, DRIPPER_GEOMETRIES } from "../types";
import type { Dripper, DripperInput } from "../types";

const EMPTY = {
  name: "", brand: "", material: "", geometry: "", url: "", image_url: "", notes: "", active: true,
};
type FormState = typeof EMPTY;

function toInput(f: FormState): DripperInput {
  const s = (v: string) => (v.trim() === "" ? null : v.trim());
  return {
    name: f.name.trim(),
    brand: s(f.brand),
    material: s(f.material),
    geometry: s(f.geometry),
    url: s(f.url),
    image_url: s(f.image_url),
    notes: s(f.notes),
    active: f.active,
  };
}

function fromDripper(d: Dripper): FormState {
  return {
    name: d.name, brand: d.brand ?? "", material: d.material ?? "", geometry: d.geometry ?? "",
    url: d.url ?? "", image_url: d.image_url ?? "", notes: d.notes ?? "", active: d.active,
  };
}

export function Drippers() {
  const { data: drippers, refresh } = useDrippers();
  const [f, setF] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState("");
  const [parsing, setParsing] = useState(false);

  const set = (k: keyof FormState, v: string | boolean) => setF((cur) => ({ ...cur, [k]: v }));
  const resetForm = () => { setF(EMPTY); setEditingId(null); setError(null); };

  const onParse = async () => {
    const u = url.trim();
    if (!/^https?:\/\/\S+\.\S+/.test(u)) { setError("paste a product URL"); return; }
    setParsing(true); setError(null);
    try {
      const p = await api.parseDripperUrl(u);
      setF((cur) => ({
        ...cur,
        name: p.name ?? cur.name,
        brand: p.brand ?? cur.brand,
        material: p.material ?? cur.material,
        geometry: p.geometry ?? cur.geometry,
        url: p.url ?? u,
        image_url: p.image_url ?? cur.image_url,
        notes: p.notes ?? cur.notes,
      }));
      setEditingId(null);
    } catch (e) { setError((e as Error).message); }
    finally { setParsing(false); }
  };

  const onSave = async () => {
    const input = toInput(f);
    if (!input.name) { setError("name is required"); return; }
    setBusy(true); setError(null);
    try {
      if (editingId != null) await api.drippers.update(editingId, input);
      else await api.drippers.create(input);
      resetForm();
      setUrl("");
      refresh();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const onEdit = (d: Dripper) => { setF(fromDripper(d)); setEditingId(d.id); setError(null); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const onDelete = async (d: Dripper) => {
    if (!confirm(`Delete "${d.name}"? Brews logged with it keep their history but lose the dripper label.`)) return;
    setBusy(true); setError(null);
    try {
      await api.drippers.remove(d.id);
      if (editingId === d.id) resetForm();
      refresh();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="eyebrow">Setup · Brewers</div>
        <h1 className="font-display text-3xl">Drippers</h1>
        <p className="text-sm text-paper-600 max-w-xl">
          Your brewer catalog. Each brew you log records which dripper you used, so a recipe or score
          is always read alongside the device it was made on. Add one by pasting a product link, or
          fill the form manually.
        </p>
      </header>

      {error ? <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div> : null}

      {/* Add / edit form */}
      <div className="border border-paper-200 p-4 space-y-4 max-w-2xl">
        <div className="eyebrow">{editingId != null ? "Edit dripper" : "Add dripper"}</div>

        {editingId == null ? (
          <div className="space-y-2">
            <label className="label">Add from product link</label>
            <div className="flex gap-2">
              <input
                type="url"
                className="input flex-1"
                placeholder="https://orea.coffee/products/v4"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <button className="btn-ghost whitespace-nowrap" disabled={parsing} onClick={onParse}>
                {parsing ? "Fetching…" : "Fetch + Parse"}
              </button>
            </div>
            <p className="text-xs text-paper-500">AI fetches the page and fills the fields below — review before saving.</p>
          </div>
        ) : null}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <label className="block col-span-2 sm:col-span-3">
            <span className="label">Name *</span>
            <input className="input" placeholder="Orea V4 narrow" value={f.name} onChange={(e) => set("name", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Brand</span>
            <input className="input" placeholder="Orea" value={f.brand} onChange={(e) => set("brand", e.target.value)} list="dripper-brands" />
          </label>
          <label className="block">
            <span className="label">Material</span>
            <input className="input" placeholder="plastic" value={f.material} onChange={(e) => set("material", e.target.value)} list="dripper-materials" />
          </label>
          <label className="block">
            <span className="label">Geometry</span>
            <input className="input" placeholder="flat" value={f.geometry} onChange={(e) => set("geometry", e.target.value)} list="dripper-geometries" />
          </label>
          <label className="block col-span-2 sm:col-span-3">
            <span className="label">Product URL</span>
            <input className="input" placeholder="https://…" value={f.url} onChange={(e) => set("url", e.target.value)} />
          </label>
          <label className="block col-span-2 sm:col-span-3">
            <span className="label">Image URL</span>
            <input className="input" placeholder="https://…/photo.jpg" value={f.image_url} onChange={(e) => set("image_url", e.target.value)} />
          </label>
          <label className="block col-span-2 sm:col-span-3">
            <span className="label">Notes</span>
            <input className="input" placeholder="flat bed, single cup" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </label>
        </div>

        <datalist id="dripper-materials">{DRIPPER_MATERIALS.map((m) => <option key={m} value={m} />)}</datalist>
        <datalist id="dripper-geometries">{DRIPPER_GEOMETRIES.map((g) => <option key={g} value={g} />)}</datalist>

        <div className="flex items-center gap-3">
          <button className="btn-primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : editingId != null ? "Save changes" : "Add dripper"}
          </button>
          <label className="flex items-center gap-2 text-sm text-paper-600">
            <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} />
            Active (show in brew form)
          </label>
          {editingId != null || url || f.name ? (
            <button type="button" className="btn-ghost" onClick={() => { resetForm(); setUrl(""); }} disabled={busy}>Cancel</button>
          ) : null}
        </div>
      </div>

      {/* Catalog list */}
      <ul className="border-t border-paper-200 max-w-2xl">
        {drippers && drippers.length === 0 ? (
          <li className="py-4 text-sm text-paper-500">No drippers yet — add your first above.</li>
        ) : null}
        {(drippers || []).map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-3 py-3 border-b border-paper-200">
            <div className="min-w-0 flex-1 basis-48">
              <div className="flex items-center gap-2">
                <span className="text-[15px] text-paper-800">{d.name}</span>
                {!d.active ? <span className="text-[10px] uppercase tracking-widest text-paper-400">inactive</span> : null}
              </div>
              <div className="text-xs text-paper-500">
                {[d.brand, d.material, d.geometry].filter(Boolean).join(" · ") || "—"}
                {d.notes ? ` · ${d.notes}` : ""}
              </div>
            </div>
            <button className="text-[10px] uppercase tracking-widest text-paper-500 hover:text-ink" onClick={() => onEdit(d)}>Edit</button>
            <button className="text-[10px] uppercase tracking-widest text-paper-400 hover:text-red-700" onClick={() => onDelete(d)} disabled={busy}>Delete</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
